import { Issue } from "@/types/issues";
import { create } from "zustand";

export interface CommandPaletteOption {
  icon: React.ReactNode;
  name: string;
  shortcut?: string[];
  action?: (value?: string) => void;
  hasFollowUp?: boolean;
  preventDefault?: boolean;
  registerKeys: string;
  subMenuConfig?: SubMenuConfig;
}

export enum CommandPaletteMode {
  DEFAULT = "default",
  ITEM_SELECTION = "item-selection",
  DATE_SELECTION = "date-selection",
}

export interface CommandPaletteSubMenuItem {
  name: string;
  value: string | null;
  icon: React.ReactNode;
}

export enum SubMenuApplicationStatus {
  NONE,
  PARTIAL,
  ALL,
}

export interface SubMenuConfig {
  mode: CommandPaletteMode;
  searchText: string;
  items: CommandPaletteSubMenuItem[];
  action: (value?: string | null) => void;
  selectionStatusFilter?: (issue: Issue, value: string | null) => boolean;
  autoClose?: boolean;
}

interface CommandPaletteStore {
  isOpen: boolean;
  options: Map<string, CommandPaletteOption[]>;
  optionsToShow: Set<string>;
  mode: CommandPaletteMode;
  subMenuConfig: SubMenuConfig | null;
  setIsOpen: (isOpen: boolean) => void;
  insertOptionCategory: (key: string, option: CommandPaletteOption[]) => void;
  removeOptionCategory: (key: string) => void;
  showOptionCategory: (key: string) => void;
  hideOptionCategory: (key: string) => void;
  setMode: (mode: CommandPaletteMode) => void;
  setSubMenuConfig: (config: SubMenuConfig | null) => void;
}

export const useCommandPaletteStore = create<CommandPaletteStore>((set) => ({
  isOpen: false,
  options: new Map(),
  optionsToShow: new Set(),
  mode: CommandPaletteMode.DEFAULT,
  subMenuConfig: null,
  setIsOpen: (isOpen) => set({ isOpen }),
  insertOptionCategory: (key, option) =>
    set({
      options: new Map(useCommandPaletteStore.getState().options).set(
        key,
        option,
      ),
    }),
  removeOptionCategory: (key) =>
    set({
      options: new Map(useCommandPaletteStore.getState().options).delete(key)
        ? new Map(useCommandPaletteStore.getState().options)
        : new Map(),
    }),
  showOptionCategory: (key) =>
    set({
      optionsToShow: new Set(
        useCommandPaletteStore.getState().optionsToShow,
      ).add(key),
    }),
  hideOptionCategory: (key) =>
    set(() => {
      const newOptionsToShow = new Set(
        useCommandPaletteStore.getState().optionsToShow,
      );
      newOptionsToShow.delete(key);
      return {
        optionsToShow: newOptionsToShow,
      };
    }),
  setMode: (mode) => set({ mode }),
  setSubMenuConfig: (config: SubMenuConfig) => set({ subMenuConfig: config }),
}));
